import {Box, Button, CTABanner} from '@primer/react-brand/esm'
import type {Document} from '@contentful/rich-text-types'

import {
  renderContentfulDownloadAction,
  renderContentfulDownloadActionFeedback,
  useResolvedDownloadOptions,
} from '@github-ui/swp-core/components/contentful/ContentfulDownloadActionMenu'
import {clsx} from 'clsx'

import {isDownloadOptions, isLink, toCoreDownloadOptions, type Action} from '../../types'
import {renderWithoutParagraphs} from '../../utils'
import baseStyles from '../../styles/base.module.css'
import styles from './FlexSuiteOutroCTA.module.css'

export type FlexSuiteOutroCTAProps = {
  outroCtaHeading?: Document
  outroCtaDescription?: Document
  outroCtaOne?: Action
  outroCtaTwo?: Action
  hasTopBorder?: boolean
  hasBottomBorder?: boolean
}

function renderOutroAction(
  action: Action | undefined,
  downloadOptions: ReturnType<typeof toCoreDownloadOptions> | undefined,
  resolvedDownloadOptions: ReturnType<typeof useResolvedDownloadOptions>,
  position: 'primary' | 'secondary',
) {
  if (!action) return null

  if (downloadOptions && resolvedDownloadOptions) {
    return renderContentfulDownloadAction({
      component: downloadOptions,
      resolved: resolvedDownloadOptions,
      analytics: {
        location: 'outro',
        context: `${position} CTA default`,
      },
      variant: position,
      elementKey: position,
    })
  }

  if (!isLink(action) || !action.fields.href) return null

  return (
    <Button key={position} as="a" href={action.fields.href} variant={position}>
      {action.fields.text}
    </Button>
  )
}

export function FlexSuiteOutroCTA({
  outroCtaHeading,
  outroCtaDescription,
  outroCtaOne,
  outroCtaTwo,
  hasTopBorder = true,
  hasBottomBorder = true,
}: FlexSuiteOutroCTAProps) {
  const outroCtaOneDownloadOptions = isDownloadOptions(outroCtaOne) ? toCoreDownloadOptions(outroCtaOne) : undefined
  const outroCtaTwoDownloadOptions = isDownloadOptions(outroCtaTwo) ? toCoreDownloadOptions(outroCtaTwo) : undefined
  const resolvedOutroCtaOneDownloadOptions = useResolvedDownloadOptions(outroCtaOneDownloadOptions)
  const resolvedOutroCtaTwoDownloadOptions = useResolvedDownloadOptions(outroCtaTwoDownloadOptions)
  const primaryAction = renderOutroAction(
    outroCtaOne,
    outroCtaOneDownloadOptions,
    resolvedOutroCtaOneDownloadOptions,
    'primary',
  )
  const secondaryAction = renderOutroAction(
    outroCtaTwo,
    outroCtaTwoDownloadOptions,
    resolvedOutroCtaTwoDownloadOptions,
    'secondary',
  )
  const actions = !primaryAction ? secondaryAction : secondaryAction ? [primaryAction, secondaryAction] : primaryAction
  const actionFeedback = (
    <>
      {renderContentfulDownloadActionFeedback(resolvedOutroCtaOneDownloadOptions)}
      {renderContentfulDownloadActionFeedback(resolvedOutroCtaTwoDownloadOptions)}
    </>
  )

  return (
    <Box className={clsx(hasTopBorder && baseStyles.gridFrameTop, hasBottomBorder && baseStyles.gridFrameBottom)}>
      <CTABanner className={styles.noBorderRadius}>
        {outroCtaHeading ? <CTABanner.Heading>{renderWithoutParagraphs(outroCtaHeading)}</CTABanner.Heading> : null}
        {outroCtaDescription ? (
          <CTABanner.Description>{renderWithoutParagraphs(outroCtaDescription)}</CTABanner.Description>
        ) : null}
        {actions ? <CTABanner.ButtonGroup>{actions}</CTABanner.ButtonGroup> : null}
        {actionFeedback}
      </CTABanner>
    </Box>
  )
}
